<ng-template
  let-extraClass="extraClass"
  #topNav>
  @if (!isRightSideContent) {
    <tidy-top-nav
      [class]="extraClass"
      [title]="'Edit Property'"
      [canGoBack]="true" />
  }
</ng-template>

<ng-container *ngTemplateOutlet="topNav;context:{ extraClass: 'mobile' }" />

<ng-template #content>
  <form [formGroup]="form">
    <!-- These templates are declared inside the form so their formControlName fields find the form group. -->

    <!-- One collapsible section. The head toggles it; the body is the template passed in. -->
    <ng-template #sectionAccordion let-section>
      <tidy-card
        class="ep-section"
        [class.ep-section--guest]="section.key === 'guest'"
        [class.ep-section--collapsed]="!section.expanded">
        <div
          class="ep-section__head"
          role="button"
          tabindex="0"
          [attr.aria-expanded]="section.expanded"
          (click)="toggleSection(section.key)"
          (keydown.enter)="toggleSection(section.key)"
          (keydown.space)="$event.preventDefault(); toggleSection(section.key)">
          <span class="ep-section__icon">
            <span
              class="ep-section__glyph"
              [style.-webkit-mask-image]="'url(' + section.icon + ')'"
              [style.mask-image]="'url(' + section.icon + ')'"></span>
          </span>
          <div class="ep-section__title">
            <tidy-text [header]="section.title" />
            <tidy-text [helper]="section.helper" />
          </div>
          @if (section.status) {
            <span
              class="ep-status"
              [class.ep-status--complete]="section.status.state === 'complete'"
              [class.ep-status--partial]="section.status.state === 'partial'">
              <tidy-text [helper]="section.status.label" />
            </span>
          }
          <tidy-image
            [src]="'assets/svg/chevron-down-outline.svg'"
            class="ep-section__chevron"
            [class.ep-section__chevron--open]="section.expanded" />
        </div>
        @if (section.expanded) {
          <div class="ep-section__body">
            <ng-container *ngTemplateOutlet="section.body" />
          </div>
        }
      </tidy-card>
    </ng-template>

    <!-- One instruction block inside a section: icon, title, helper and the Add Photo action. -->
    <ng-template #blockHead let-block>
      <div class="ep-block__head">
        <tidy-image
          [src]="block.icon"
          class="title-size" />
        <div class="ep-block__title">
          <tidy-text [title]="block.title" />
          <tidy-text [helper]="block.helper" />
        </div>
        @if (block.photoType) {
          <tidy-text
            [body]="'Add Photo'"
            (action)="getImg(block.photoType)"
            class="link ep-block__action" />
        }
      </div>
    </ng-template>

    <ng-template #proInstructions>
      <div class="ep-block">
        <ng-container *ngTemplateOutlet="blockHead; context: { $implicit: { icon: 'assets/img/car.svg', title: 'How to Park', helper: 'Where to park and how it is paid for.', photoType: 'parking' } }" />
        <div class="ep-fields">
          <tidy-select
            [icon]="'assets/svg/car.svg'"
            [label]="'What kind of parking?'"
            [form]="form.controls.paid_parking"
            formControlName="paid_parking"
            [items]="parkingTypes" />
          <tidy-select
            [icon]="'assets/svg/location.svg'"
            [label]="'What kind of spot?'"
            [form]="form.controls.parking_spot"
            formControlName="parking_spot"
            [items]="form.controls.paid_parking.value ? paidParkingSpots : freeParkingSpots" />
          @if (form.controls.paid_parking.value) {
            <tidy-select
              [icon]="'assets/svg/card.svg'"
              [label]="'How will you pay?'"
              [form]="form.controls.parking_pay_with"
              formControlName="parking_pay_with"
              [items]="payOptions" />
            <tidy-select
              [icon]="'assets/svg/cash.svg'"
              [label]="'How much will you reimburse?'"
              [form]="form.controls.max_parking_cost"
              formControlName="max_parking_cost"
              [items]="maxCostOptions" />
          }
          <tidy-input
            class="ep-fields__wide"
            [icon]="'assets/svg/create.svg'"
            [label]="'Any notes to help park?'"
            formControlName="parking_notes"
            [form]="form.controls.parking_notes" />
        </div>
        <photo-caption
          [photoUrls]="parkingPhotoUrls"
          (deletePhoto)="confirmDelete($event)"
          [type]="'parking'" />
      </div>
      <div class="ep-block">
        <ng-container *ngTemplateOutlet="blockHead; context: { $implicit: { icon: 'assets/img/unlock.svg', title: 'How to Get In', helper: 'Door codes, lockboxes and keys.', photoType: 'access' } }" />
        <tidy-textarea
          [icon]="'assets/svg/key.svg'"
          [label]="'How do they access the property?'"
          formControlName="home_access"
          [form]="form.controls.home_access" />
        <photo-caption
          [photoUrls]="accessPhotoUrls"
          (deletePhoto)="confirmDelete($event)"
          [type]="'access'" />
      </div>
      <div class="ep-block">
        <ng-container *ngTemplateOutlet="blockHead; context: { $implicit: { icon: 'assets/img/lock.svg', title: 'How to Close Up', helper: 'What to lock, turn off or reset on the way out.', photoType: 'closing' } }" />
        <tidy-textarea
          [icon]="'assets/svg/lock-closed.svg'"
          [label]="'What should they do as they leave?'"
          formControlName="home_close"
          [form]="form.controls.home_close" />
        <photo-caption
          [photoUrls]="closingPhotoUrls"
          (deletePhoto)="confirmDelete($event)"
          [type]="'closing'" />
      </div>
    </ng-template>

    <ng-template #guestInstructions>
      <div class="ep-block">
        <ng-container *ngTemplateOutlet="blockHead; context: { $implicit: { icon: 'assets/img/car.svg', title: 'How to Park', helper: 'Where to park and how it is paid for.', photoType: 'guest_parking' } }" />
        <div class="ep-fields">
          <tidy-select
            [icon]="'assets/svg/car.svg'"
            [label]="'What kind of parking?'"
            [form]="form.controls.guest_paid_parking"
            formControlName="guest_paid_parking"
            [items]="parkingTypes" />
          <tidy-select
            [icon]="'assets/svg/location.svg'"
            [label]="'What kind of spot?'"
            [form]="form.controls.guest_parking_spot"
            formControlName="guest_parking_spot"
            [items]="form.controls.guest_paid_parking.value ? paidParkingSpots : freeParkingSpots" />
          @if (form.controls.guest_paid_parking.value) {
            <tidy-select
              [icon]="'assets/svg/card.svg'"
              [label]="'How do guests pay?'"
              [form]="form.controls.guest_parking_pay_with"
              formControlName="guest_parking_pay_with"
              [items]="payOptions" />
            <tidy-select
              [icon]="'assets/svg/cash.svg'"
              [label]="'Max parking cost'"
              [form]="form.controls.guest_max_parking_cost"
              formControlName="guest_max_parking_cost"
              [items]="maxCostOptions" />
          }
          <tidy-textarea
            class="ep-fields__wide"
            [icon]="'assets/svg/create.svg'"
            [label]="'Any notes to help park?'"
            formControlName="guest_parking_notes"
            [form]="form.controls.guest_parking_notes" />
        </div>
        <photo-caption
          [photoUrls]="guestParkingPhotoUrls"
          (deletePhoto)="confirmDelete($event)"
          [type]="'guest_parking'" />
      </div>
      <div class="ep-block">
        <ng-container *ngTemplateOutlet="blockHead; context: { $implicit: { icon: 'assets/img/unlock.svg', title: 'How to Get In', helper: 'Door codes, lockboxes and keys.', photoType: 'guest_access' } }" />
        <tidy-textarea
          [icon]="'assets/svg/key.svg'"
          [label]="'How do guests get in?'"
          formControlName="guest_home_access"
          [form]="form.controls.guest_home_access" />
        <photo-caption
          [photoUrls]="guestAccessPhotoUrls"
          (deletePhoto)="confirmDelete($event)"
          [type]="'guest_access'" />
      </div>
      @if (showCheckoutChecklist) {
        <div class="ep-block">
          <ng-container *ngTemplateOutlet="blockHead; context: { $implicit: { icon: 'assets/img/lock.svg', title: 'Checkout Checklist', helper: 'Kept in sync with the Checkout card in your guidebook.' } }" />
          <tidy-textarea
            [icon]="'assets/svg/lock-closed.svg'"
            [label]="'What should guests do before they leave?'"
            formControlName="check_out_instructions"
            [form]="form.controls.check_out_instructions" />
        </div>
      }
    </ng-template>

    <ng-template #customFields>
      <div class="ep-block">
        <div class="ep-fields">
          @for (field of customFieldDefinitions; track field.id) {
            @if (field.field_type === 'string') {
              <tidy-input
                [label]="field.field_name"
                [icon]="'assets/svg/create.svg'"
                [ngModel]="customFieldValues[field.field_key]"
                [ngModelOptions]="{standalone: true}"
                (ngModelChange)="onCustomFieldValueChange(field.field_key, $event)" />
            }
            @if (field.field_type === 'integer') {
              <tidy-input
                [label]="field.field_name"
                [icon]="'assets/svg/calculator.svg'"
                [type]="'number'"
                [ngModel]="customFieldValues[field.field_key]"
                [ngModelOptions]="{standalone: true}"
                (ngModelChange)="onCustomFieldValueChange(field.field_key, $event)" />
            }
            @if (field.field_type === 'boolean') {
              <div class="ep-toggle-field">
                <tidy-text [body]="field.field_name" />
                <tidy-toggle
                  [toggled]="customFieldValues[field.field_key] === true"
                  (toggleChanged)="onCustomFieldValueChange(field.field_key, $event)" />
              </div>
            }
          }
        </div>
      </div>
    </ng-template>

    <tidy-wrapper [class]="isRightSideContent ? 'right-side-scroll-enabled' : ''">
      @if (loaded) {
        <div class="ep-page">
          <div class="ep-hero">
            @if (address) {
              <tidy-text
                [header]="address.address_name || address.address1"
                [translate]="false" />
              @if (addressLine) {
                <tidy-text
                  [helper]="addressLine"
                  [translate]="false" />
              }
            }
            <tidy-text
              class="ep-hero__intro"
              [body]="'This information is important to help your Pro get in, and never used for marketing. The more detail the better. Photos help a lot!'" />
          </div>

          <tidy-card class="ep-name-card">
            <tidy-input
              formControlName="propertyName"
              [form]="form.controls.propertyName"
              [label]="'Property Name'"
              [errorMessage]="'Please enter a property name.'"
              [submitted]="submitted"
              [icon]="'assets/svg/home.svg'" />
          </tidy-card>

          <ng-container *ngTemplateOutlet="sectionAccordion; context: { $implicit: {
            key: 'pro',
            title: 'For your Pro',
            helper: isRentalClient ? 'Only your Pro sees these. Never shown to guests.' : 'How your Pro parks, gets in and closes up.',
            icon: 'assets/svg/construct-outline.svg',
            status: sectionStatus.pro,
            expanded: expandedSections.pro,
            body: proInstructions
          } }" />

          @if (isRentalClient) {
            <ng-container *ngTemplateOutlet="sectionAccordion; context: { $implicit: {
              key: 'guest',
              title: 'For your guests',
              helper: 'Guests see these in the guidebook.',
              icon: 'assets/svg/people-outline.svg',
              status: sectionStatus.guest,
              expanded: expandedSections.guest,
              body: guestInstructions
            } }" />
          }

          @if (customFieldDefinitions.length > 0) {
            <ng-container *ngTemplateOutlet="sectionAccordion; context: { $implicit: {
              key: 'custom',
              title: 'Custom Fields',
              helper: 'Extra details your team tracks for this property.',
              icon: 'assets/svg/options-outline.svg',
              status: null,
              expanded: expandedSections.custom,
              body: customFields
            } }" />
          }

          <div class="ep-footer">
            <tidy-text
              [body]="'Delete Property'"
              (action)="deleteAddress()"
              class="link red" />
            <tidy-button
              [text]="'Save Changes'"
              [action]="boundSave"
              class="primary" />
          </div>
          <tidy-error-message [text]="errorMessage" />
        </div>
      }
    </tidy-wrapper>
  </form>
</ng-template>

@if (!isRightSideContent) {
  <app-scroll-wrapper [templateContent]="content" />
}

@if (isRightSideContent) {
  <ion-content class="ion-content-right-column">
    <ng-container *ngTemplateOutlet="content" />
  </ion-content>
}
